import { Component } from '@theme/component';
import { ThemeEvents, ZoomMediaSelectedEvent } from '@theme/events';
import { StandardEvents, ProductSelectEvent } from '@shopify/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(StandardEvents.productSelect, this.#handleProductSelect, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    this.#setupScrollHint(signal);
  }

  #controller = new AbortController();
  /** @type {ResizeObserver | undefined} */
  #scrollHintObserver;

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#scrollHintObserver?.disconnect();
  }

  /** Show the cue only for an overflowing desktop product gallery. */
  #setupScrollHint(signal) {
    const hint = this.querySelector('[data-gallery-scroll-hint]');
    const scrollArea = this.closest('.product-information__media');
    if (!(hint instanceof HTMLElement) || !(scrollArea instanceof HTMLElement)) return;

    const desktop = window.matchMedia('(min-width: 990px) and (hover: hover) and (pointer: fine)');
    let dismissed = scrollArea.scrollTop > 16;
    const update = () => {
      const hasMoreMedia = (this.media?.length ?? 0) > 1;
      const canScroll = scrollArea.scrollHeight > scrollArea.clientHeight + 16;
      const visibleBottom = Math.min(scrollArea.getBoundingClientRect().bottom, window.innerHeight);
      const hintTop = visibleBottom - this.getBoundingClientRect().top - 24;
      this.style.setProperty('--gilas-gallery-hint-top', `${hintTop}px`);
      hint.hidden = dismissed || !desktop.matches || !hasMoreMedia || !canScroll;
    };

    scrollArea.addEventListener('scroll', () => {
      if (scrollArea.scrollTop > 16) {
        dismissed = true;
        hint.hidden = true;
      }
    }, { passive: true, signal });
    desktop.addEventListener('change', update, { signal });
    window.addEventListener('scroll', update, { passive: true, signal });
    this.#scrollHintObserver = new ResizeObserver(update);
    this.#scrollHintObserver.observe(scrollArea);
    this.#scrollHintObserver.observe(this);
    update();
  }

  /**
   * Handles a product select event by replacing the current media gallery with a new one.
   *
   * @param {ProductSelectEvent} event - The product select event.
   */
  #handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    event.promise
      .then(({ detail }) => {
        if (!detail?.html) return;

        const { html } = detail;
        const newMediaGallery = html.querySelector('media-gallery');
        if (!newMediaGallery) return;

        this.replaceWith(newMediaGallery);
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[media-gallery] Event promise rejected:', error);
      });
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
